import React, { useMemo } from 'react';
import Box from 'apollo-react/components/Box';
import Typography from 'apollo-react/components/Typography';
import Divider from 'apollo-react/components/Divider';
import CommentsList from '../CommentsList/CommentsList';
import CommentToggle from '../../CommentToggle/CommentToggle';
import axiosInstance from '../../../../axiosInstance';

/**
 * Admin (and optional end-user) comments with attachments for one HCP profile main-menu section.
 * Mirrors {@link HcpLicensesListView} comment UX without duplicating profile fields.
 */
const HcpProfileSectionReviewerPanel = ({
  persona = 'admin',
  activeSectionKey,
  requestProgressId,
  /** Progress row used for POST (defaults to URL progress; may be latest submitted). */
  commentProgressId = null,
  requestId,
  sectionComments = [],
  onRefresh,
  enableAddComment = true,
}) => {
  const isInternal = persona !== 'enduser';
  const writeProgressId = commentProgressId ?? requestProgressId;

  const sortedSectionComments = useMemo(
    () =>
      (sectionComments || []).filter(
        (c) => String(c.profile_section_key) === String(activeSectionKey)
      ),
    [sectionComments, activeSectionKey]
  );

  const handleAddComment = async (
    comment,
    _subFormId,
    fileId = null,
    fileData = null
  ) => {
    try {
      const requestData = {
        request_progress_id: writeProgressId,
        request_id: requestId,
        profile_section_key: activeSectionKey,
        comment,
      };
      if (fileId) {
        requestData.additional_file_id = fileId;
      }

      const endpoint = isInternal
        ? '/api/hcp-profile-section-comments'
        : '/api/hcp-profile-section-comments/enduser';
      const response = await axiosInstance.post(endpoint, requestData);

      const enrichedComment = { ...response.data, isOwner: true };
      if (fileData && fileId) {
        enrichedComment.additional_file = {
          id: fileData.id,
          original_name: fileData.original_name,
          file_path: null,
          file_size: fileData.file_size,
          mime_type: fileData.mime_type,
          createdAt: fileData.created_at,
          updatedAt: fileData.created_at,
          deletedAt: null,
          user_id: null,
          end_user_id: null,
          ...fileData,
        };
      }

      if (onRefresh) {
        await onRefresh();
      }
      return enrichedComment;
    } catch (error) {
      console.error('hcp profile section comment add:', error);
      throw error;
    }
  };

  const handleEditComment = async (commentId, newText, attachmentInfo = {}) => {
    try {
      const requestData = { comment: newText };
      if (attachmentInfo.remove_attachment) {
        requestData.remove_attachment = true;
      } else if (attachmentInfo.new_file_id) {
        requestData.additional_file_id = attachmentInfo.new_file_id;
      }

      const endpoint = isInternal
        ? `/api/hcp-profile-section-comments/${commentId}`
        : `/api/hcp-profile-section-comments/enduser/${commentId}`;

      await axiosInstance.put(endpoint, requestData);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('hcp profile section comment edit:', error);
      throw error;
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const endpoint = isInternal
        ? `/api/hcp-profile-section-comments/${commentId}`
        : `/api/hcp-profile-section-comments/enduser/${commentId}`;
      await axiosInstance.delete(endpoint);
      if (onRefresh) await onRefresh();
    } catch (error) {
      console.error('hcp profile section comment delete:', error);
      throw error;
    }
  };

  if (!activeSectionKey || !writeProgressId) {
    return null;
  }

  const hasComments = sortedSectionComments.length > 0;
  const showAdminAddComment = enableAddComment && isInternal;
  const showCustomerAddComment = enableAddComment && !isInternal;
  const canShowAdd = showAdminAddComment || showCustomerAddComment;

  if (!hasComments && !canShowAdd) {
    return null;
  }

  return (
    <div
      className="form-comment-section vertical-tab-panel"
      style={{
        marginTop: 35,
        isolation: 'isolate',
        position: 'relative',
        zIndex: 1,
      }}
    >
      {hasComments && (
        <>
          <Typography
            variant="title1"
            component="h2"
            sx={{
              fontWeight: 500,
              marginBottom: 3,
              color: '#333',
              paddingBottom: 1,
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            Comments
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {!hasComments && canShowAdd ? (
        <Typography
          variant="title1"
          component="h2"
          sx={{
            fontWeight: 500,
            marginBottom: 2,
            color: '#333',
            paddingBottom: 1,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          Comments
        </Typography>
      ) : null}

      {hasComments && (
        <CommentsList
          prevComments={sortedSectionComments}
          pagination={null}
          showControls={false}
          showFilters={false}
          showSorting={false}
          showPageSize={false}
          showResultsInfo={false}
          showPagination={false}
          showCommentsTitle={false}
          persona={persona}
          onEdit={handleEditComment}
          onDelete={handleDeleteComment}
        />
      )}

      {showAdminAddComment ? (
        <Box sx={{ mt: 2 }}>
          <CommentToggle
            onAddComment={handleAddComment}
            persona="admin"
            buttonText="Add Comment"
            buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
            hasComments={hasComments}
          />
        </Box>
      ) : null}
      {showCustomerAddComment ? (
        <Box sx={{ mt: 2 }}>
          <CommentToggle
            onAddComment={handleAddComment}
            persona="enduser"
            subFormId={activeSectionKey}
            buttonText="Comment"
            buttonProps={{ sx: { marginTop: 2, marginBottom: 2 } }}
            hasComments={hasComments}
          />
        </Box>
      ) : null}
    </div>
  );
};

export default HcpProfileSectionReviewerPanel;
